/**
 * Message reader — the detail pane.
 *
 * HTML bodies never touch this document: they are sanitised
 * (see `sanitizeEmailHtml`) and rendered inside an iframe sandboxed without
 * `allow-scripts` or `allow-same-origin`, so a sanitiser bypass still cannot
 * execute script or reach this origin's storage.
 */
import type { JSX } from "preact";

import type { Address, Attachment, ParsedMessage } from "../engine/types.ts";
import { openMessage, toggleFlag } from "../state/session.ts";
import {
  activeMessage,
  activeMessageId,
  composeRequest,
  messageError,
  messageLoading,
  messages,
} from "../state/store.ts";

import { formatFullDate, formatSize } from "./format.ts";
import { FlagIcon, MailIcon } from "./icons.tsx";
import { sanitizeEmailHtml } from "./sanitize.ts";

/**
 * Renders the selected message, or explains why the pane is empty.
 *
 * @returns The reader section element.
 */
export function MessageReader(): JSX.Element {
  const message = activeMessage.value;
  if (message === null) {
    if (activeMessageId.value === null) {
      return (
        <div class="empty-state" role="note">
          <MailIcon />
          <p>Select a message to read it here.</p>
        </div>
      );
    }
    if (messageLoading.value) {
      return (
        <div class="empty-state" role="status">
          <p>Fetching the message…</p>
        </div>
      );
    }
    return (
      <div class="empty-state">
        {messageError.value === null ? null : (
          <p class="pane-alert" role="alert">
            {messageError.value}
          </p>
        )}
        <button
          type="button"
          class="ghost-button"
          onClick={() => {
            void openMessage(activeMessageId.value ?? 0);
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  return (
    <article class="message-reader" aria-label="Message">
      <ReaderToolbar />
      <header class="message-header">
        <h2 class="reader-subject">{message.subject ?? "(no subject)"}</h2>
        <HeaderRow label="From" addresses={message.from} />
        {message.to.length > 0 ? (
          <HeaderRow label="To" addresses={message.to} />
        ) : null}
        {message.cc.length > 0 ? (
          <HeaderRow label="Cc" addresses={message.cc} />
        ) : null}
        {message.date === null ? null : (
          <p class="reader-date">
            <time datetime={new Date(message.date * 1000).toISOString()}>
              {formatFullDate(message.date, message.date_offset)}
            </time>
          </p>
        )}
      </header>
      <MessageBody message={message} />
      {message.attachments.length > 0 ? (
        <Attachments attachments={message.attachments} />
      ) : null}
      {message.headers.length > 0 ? (
        <RawHeaders headers={message.headers} />
      ) : null}
    </article>
  );
}

/**
 * Reader actions: reply and flag, driven by the list's flag state. The
 * toolbar renders only alongside a message, so the selection is live.
 *
 * @returns The toolbar element.
 */
function ReaderToolbar(): JSX.Element {
  const uid = activeMessageId.value ?? -1;
  const isFlagged =
    messages.value.find((row) => row.id === uid)?.flagged === true;
  return (
    <div class="reader-toolbar">
      <button
        type="button"
        class="ghost-button"
        onClick={() => {
          composeRequest.value = { mode: "reply", uid };
        }}
      >
        Reply
      </button>
      <button
        type="button"
        class="ghost-button"
        aria-pressed={isFlagged}
        onClick={() => {
          void toggleFlag(uid, String.raw`\Flagged`, !isFlagged);
        }}
      >
        <FlagIcon filled={isFlagged} />
        {isFlagged ? "Flagged" : "Flag"}
      </button>
    </div>
  );
}

/**
 * One labelled address row ("From", "To", …).
 *
 * @param row - Label and the mailboxes to show.
 * @param row.label - The visible label ("From", "To", …).
 * @param row.addresses - Parsed mailboxes for the row.
 * @returns The header row element.
 */
function HeaderRow(row: {
  readonly label: string;
  readonly addresses: readonly Address[];
}) {
  return (
    <p class="header-row">
      <span class="header-label">{row.label}</span>
      <AddressList addresses={row.addresses} />
    </p>
  );
}

/**
 * Renders parsed mailboxes; each safe address becomes a `mailto:` link.
 *
 * `mailto:` cannot run script, but addresses outside the sane RFC 5322 shape
 * (obsolete quoting, spaces) are rendered as plain text so a hostile parsed
 * value cannot smuggle anything into an href.
 *
 * @param list - Mailboxes to render.
 * @param list.addresses - The mailboxes themselves.
 * @returns The address list element.
 */
function AddressList(list: {
  readonly addresses: readonly Address[];
}): JSX.Element {
  return (
    <span class="address-list">
      {list.addresses.map((address, index) => {
        const name =
          address.display_name === null ? null : (
            <span class="address-name">{address.display_name} </span>
          );
        return (
          <span class="address" key={`${address.address}#${index}`}>
            {name}
            {isPlainAddress(address.address) ? (
              <a href={`mailto:${address.address}`}>{address.address}</a>
            ) : (
              address.address
            )}
          </span>
        );
      })}
    </span>
  );
}

/**
 * True for a conservative `local@domain` shape that is safe to put in an
 * href.
 *
 * @param address - Parsed address text.
 * @returns Whether it may be used verbatim in a `mailto:` link.
 */
function isPlainAddress(address: string): boolean {
  return /^[^\s<>,;:"@]+@[^\s,:;<>@][^\s,.:;<>@]*\.[^\s,:;<>@]+$/u.test(
    address,
  );
}

/**
 * Renders the body: sanitised HTML in a sandboxed iframe, or plain text.
 *
 * @param body - The message whose body to render.
 * @param body.message - The parsed message.
 * @returns The body element.
 */
function MessageBody(body: { readonly message: ParsedMessage }) {
  const { body_text: bodyText, body_is_html: bodyIsHtml } = body.message;
  if (bodyText === null) {
    return <p class="empty-body">This message has no displayable body.</p>;
  }
  if (bodyIsHtml) {
    return (
      <iframe
        class="message-frame"
        title="Message body"
        // No allow-scripts, no allow-same-origin: the frame cannot execute
        // code or read this origin. Popups stay available so links can open
        // in a new tab, which is standard webmail behaviour.
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcdoc={frameDocument(sanitizeEmailHtml(bodyText))}
      />
    );
  }
  return <div class="message-text">{bodyText}</div>;
}

/**
 * Wraps sanitised body HTML in a minimal standalone document.
 *
 * @param bodyHtml - Already-sanitised body markup.
 * @returns A full HTML document for the iframe's `srcdoc`.
 */
function frameDocument(bodyHtml: string): string {
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    // Every link opens in a new top-level context; combined with the sandbox
    // this keeps navigation away from the app itself.
    '<base target="_blank">',
    "<style>",
    ":root { color-scheme: light dark; }",
    "body { margin: 1rem; font: 16px/1.5 system-ui, sans-serif; overflow-wrap: anywhere; }",
    "img { max-width: 100%; height: auto; }",
    "a { color: inherit; }",
    "</style></head><body>",
    bodyHtml,
    "</body></html>",
  ].join("");
}

/**
 * Lists attachments with their real metadata. Download links arrive with the
 * network bridge; until then the metadata itself is the honest content.
 *
 * @param files - Attachment metadata.
 * @param files.attachments - The attachments themselves.
 * @returns The attachments section element.
 */
function Attachments(files: { readonly attachments: readonly Attachment[] }) {
  return (
    <section
      class="attachments"
      aria-label={`Attachments (${files.attachments.length})`}
    >
      <h3 class="section-title">Attachments</h3>
      <ul class="attachment-list">
        {files.attachments.map((attachment, index) => (
          <li class="attachment" key={`${attachment.filename ?? ""}#${index}`}>
            <span class="attachment-name">
              {attachment.filename ?? "Unnamed attachment"}
            </span>
            <span class="attachment-meta">
              {attachment.mime_type} · {formatSize(attachment.size)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Collapsible dump of every header, in original wire order.
 *
 * @param headers - `[name, value]` pairs as parsed from the message.
 * @param headers.headers - The pairs themselves.
 * @returns The details element.
 */
function RawHeaders(headers: {
  readonly headers: readonly (readonly [string, string])[];
}) {
  return (
    <details class="raw-headers">
      <summary>All headers</summary>
      <dl class="header-dump">
        {headers.headers.map(([name, value], index) => (
          <div class="header-entry" key={`${name}#${index}`}>
            <dt>{name}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
